import type { RenderObject, Vec3 } from '@v2/contracts'

const cursor = (fill: string) => `url("data:image/svg+xml,${encodeURIComponent(`<svg xmlns="http://www.w3.org/2000/svg" width="28" height="32" viewBox="0 0 28 32"><path d="M4 2 23 19l-9 1-4 9Z" fill="${fill}" stroke="#182b2a" stroke-width="2" stroke-linejoin="round"/><path d="m7 7 4 14" stroke="white" stroke-opacity=".65" stroke-width="1.5"/></svg>`)}") 4 2, default`
export const TABLE_CURSOR = cursor('#f3e9d3')
export const ACTION_CURSOR = cursor('#c9efff')

/** Ephemeral, world-space attention cues; never enter game state or picking. */
export class TableFeedback {
  muted = false
  globallyMuted = false
  private audio: AudioContext | null = null
  private pings: { position: Vec3; at: number; id: number }[] = []
  private serial = 0
  private lastPing = -Infinity
  constructor(private now = () => performance.now()) {}
  add(position: Vec3): boolean {
    const now = this.now()
    if (!position.every(value => Number.isFinite(value) && Math.abs(value) <= 1000) || now - this.lastPing < 250) return false
    this.lastPing = now
    this.pings = [...this.pings.slice(-7), { position, at: now, id: this.serial++ }]
    this.sound()
    return true
  }
  sample(reducedMotion: boolean): RenderObject[] {
    const now = this.now()
    this.pings = this.pings.filter(ping => now - ping.at < 2400)
    return this.pings.flatMap(ping => {
      const age = (now - ping.at) / 1000
      const radius = reducedMotion ? 0.45 : 0.35 + 0.12 * Math.sin(age * 7)
      const y = ping.position[1] + 0.18
      const common = { rotation: 0, color: '#ff7055', decorative: true, roughness: 0.4 }
      return [
        ...Array.from({ length: 20 }, (_, i): RenderObject => ({ ...common, id: `__ping:${ping.id}:${i}`, shape: 'sphere', position: [ping.position[0] + Math.cos(i * Math.PI / 10) * radius, y, ping.position[2] + Math.sin(i * Math.PI / 10) * radius], size: [0.11, 0.11, 0.11] })),
        { ...common, id: `__ping:${ping.id}:stem`, shape: 'slab' as const, position: [ping.position[0], y + 0.55, ping.position[2]] as Vec3, size: [0.12, 0.48, 0.12] as Vec3 },
        { ...common, id: `__ping:${ping.id}:dot`, shape: 'sphere' as const, position: [ping.position[0], y + 0.17, ping.position[2]] as Vec3, size: [0.14, 0.14, 0.14] as Vec3 },
      ]
    })
  }
  /** Unlock on a local gesture so subsequent remote cues can sound too. */
  unlock = (): void => {
    if (this.muted || this.globallyMuted || typeof AudioContext === 'undefined') return
    try { this.audio ??= new AudioContext(); void this.audio.resume().catch(() => {}) } catch { /* Audio is optional. */ }
  }
  private sound(): void {
    if (this.muted || this.globallyMuted) return
    this.unlock()
    const audio = this.audio
    if (!audio || audio.state !== 'running') return
    const tone = audio.createOscillator(), gain = audio.createGain()
    tone.type = 'sine'
    tone.frequency.setValueAtTime(880, audio.currentTime)
    tone.frequency.exponentialRampToValueAtTime(660, audio.currentTime + 0.16)
    gain.gain.setValueAtTime(0.0001, audio.currentTime)
    gain.gain.exponentialRampToValueAtTime(0.09, audio.currentTime + 0.012)
    gain.gain.exponentialRampToValueAtTime(0.0001, audio.currentTime + 0.28)
    tone.connect(gain); gain.connect(audio.destination)
    tone.onended = () => { tone.disconnect(); gain.disconnect() }
    tone.start(); tone.stop(audio.currentTime + 0.3)
  }
  clear(): void { this.pings = []; this.lastPing = -Infinity }
  dispose(): void { this.clear(); void this.audio?.close().catch(() => {}); this.audio = null }
}
